import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X, Wifi, WifiOff, RefreshCw } from 'lucide-react';

// ===========================
// PWA Install Prompt
// ===========================
export const PwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already dismissed recently
    const dismissedAt = localStorage.getItem('ci_pwa_dismissed');
    if (dismissedAt && Date.now() - parseInt(dismissedAt) < 7 * 24 * 60 * 60 * 1000) {
      setDismissed(true);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after user has been on the site for 30 seconds
      setTimeout(() => {
        if (!dismissed) setShowPrompt(true);
      }, 30000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [dismissed]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('ci_pwa_dismissed', Date.now().toString());
  };

  if (!showPrompt || dismissed || !deferredPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50 animate-in slide-in-from-bottom-5 duration-300" data-testid="pwa-install-prompt">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 relative">
        <button onClick={handleDismiss} className="absolute top-3 right-3 text-slate-400 hover:text-slate-600" data-testid="pwa-dismiss-btn">
          <X className="h-4 w-4" />
        </button>
        <div className="flex gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#2a3e5d] flex items-center justify-center shrink-0">
            <Download className="h-6 w-6 text-[#02d1d4]" />
          </div>
          <div className="flex-1 pr-4">
            <h3 className="font-semibold text-sm text-[#2a3e5d]">Install Common Impact</h3>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              Add to your home screen for quick access and offline learning.
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button onClick={handleInstall} size="sm" className="bg-[#20acf6] hover:bg-[#3bb8f7] text-xs flex-1" data-testid="pwa-install-btn">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Install App
          </Button>
          <Button onClick={handleDismiss} size="sm" variant="ghost" className="text-xs text-slate-500">
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
};

// ===========================
// Offline Indicator
// ===========================
export const OfflineIndicator = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => {
      setIsOffline(false);
    };

    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!isOffline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[70] bg-amber-500 text-white text-center py-1.5 px-4 text-xs font-medium flex items-center justify-center gap-2" data-testid="offline-indicator">
      <WifiOff className="h-3.5 w-3.5" />
      <span>You're offline — some features may be limited</span>
    </div>
  );
};

// ===========================
// Update Available Banner
// ===========================
export const UpdateBanner = () => {
  const [showUpdate, setShowUpdate] = useState(false);

  useEffect(() => {
    const handler = () => setShowUpdate(true);
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, []);

  const handleUpdate = () => {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    window.location.reload();
  };

  if (!showUpdate) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50" data-testid="update-banner">
      <div className="bg-[#2a3e5d] text-white rounded-xl shadow-lg p-4 flex items-center gap-3">
        <RefreshCw className="h-5 w-5 text-[#02d1d4] shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium">Update available</p>
          <p className="text-xs text-white/70">Refresh to get the latest version.</p>
        </div>
        <Button onClick={handleUpdate} size="sm" className="bg-[#20acf6] hover:bg-[#3bb8f7] text-xs shrink-0">
          Refresh
        </Button>
      </div>
    </div>
  );
};
